/**
 * Admin Layout Component
 * Adapted from HappyHippo AdminLayout pattern
 */

import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useStatus } from '../contexts/StatusContext';
import LocalEnvironmentBadge from './LocalEnvironmentBadge';
import WeeklySplash from './WeeklySplash';
import { claimsApi } from '../api/claims';
import { capturesApi } from '../api/captures';
import { dispatchesApi } from '../api/dispatches';
import {
  HiHome,
  HiChartBar,
  HiUsers,
  HiCreditCard,
  HiFolderOpen,
  HiShieldCheck,
  HiClipboardList,
  HiLightBulb,
  HiCog,
  HiLogout,
  HiUserGroup,
  HiBeaker,
  HiTrendingUp,
  HiGlobeAlt,
  HiKey,
  HiDatabase,
  HiDocumentText,
  HiPencilAlt,
  HiDuplicate,
  HiAnnotation,
  HiInbox,
  HiSwitchHorizontal,
} from 'react-icons/hi';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  feature?: string;
  badgeKey?: string;
}

const AdminLayout: React.FC = () => {
  const { user, client, logout, hasFeature, isLoading } = useStatus();
  const location = useLocation();
  const navigate = useNavigate();
  const [showSplash, setShowSplash] = useState(true);
  const [badgeCounts, setBadgeCounts] = useState<Record<string, number>>({});

  // Account controls and partner invitation/context selection remain reachable on free accounts.
  const tier = client?.subscription_tier || 'free';
  const allowedFreePaths = ['/subscriptions', '/settings', '/api-tokens', '/proposals'];
  const isFreeTier = tier === 'free';

  useEffect(() => {
    if (!isLoading && isFreeTier && !allowedFreePaths.some(p => location.pathname === p || location.pathname.startsWith(`${p}/`))) {
      navigate('/subscriptions', { replace: true });
    }
  }, [isLoading, isFreeTier, location.pathname, navigate]);

  useEffect(() => {
    if (isLoading || isFreeTier) return;
    const loadBadges = async () => {
      try {
        const [claimsRes, capturesRes, dispatchesRes] = await Promise.all([
          claimsApi.getCounts(),
          capturesApi.list('pending'),
          dispatchesApi.badgeCount(),
        ]);
        const counts: Record<string, number> = {};
        if (claimsRes.success && claimsRes.data) {
          const data = (claimsRes.data as any).data || claimsRes.data;
          counts.claims = parseInt(data.status_counts?.proposed) || 0;
        }
        if (capturesRes.success && capturesRes.data) {
          const data = (capturesRes.data as any).data || capturesRes.data;
          counts.captures = data.count || 0;
        }
        if (dispatchesRes.success && dispatchesRes.data) {
          const data = (dispatchesRes.data as any).data || dispatchesRes.data;
          counts.dispatches = data.badge_total ?? data.pagination?.total ?? 0;
        }
        setBadgeCounts(counts);
      } catch { /* badge counts are supplementary */ }
    };
    loadBadges();
    window.addEventListener('mindmeld:dispatches-updated', loadBadges);
    return () => window.removeEventListener('mindmeld:dispatches-updated', loadBadges);
  }, [isLoading, isFreeTier]);

  const navItems: NavItem[] = [
    { name: 'Dashboard', href: '/dashboard', icon: HiHome },
    { name: 'Manager View', href: '/manager', icon: HiUserGroup, feature: 'analytics' },
    { name: 'Reports', href: '/reports', icon: HiChartBar, feature: 'analytics' },
    { name: 'Users', href: '/users', icon: HiUsers },
    { name: 'Subscription', href: '/subscriptions', icon: HiCreditCard },
    { name: 'Projects', href: '/projects', icon: HiFolderOpen },
    { name: 'Standards', href: '/standards', icon: HiShieldCheck },
    { name: 'Corpus', href: '/corpus', icon: HiDatabase, feature: 'audit_trail' },
    { name: 'Documents', href: '/documents', icon: HiDocumentText, feature: 'audit_trail' },
    { name: 'Standards Curation', href: '/curation', icon: HiDuplicate, feature: 'audit_trail' },
    { name: 'Claims Review', href: '/claims', icon: HiAnnotation, feature: 'audit_trail', badgeKey: 'claims' },
    { name: 'Capture Review', href: '/captures', icon: HiInbox, feature: 'audit_trail', badgeKey: 'captures' },
    { name: 'Discovery Review', href: '/review', icon: HiBeaker, feature: 'analytics' },
    { name: 'Corrections', href: '/corrections', icon: HiPencilAlt, feature: 'audit_trail' },
    { name: 'Design Proposals', href: '/proposals', icon: HiDocumentText },
    { name: 'Dispatches', href: '/dispatches', icon: HiSwitchHorizontal, feature: 'audit_trail', badgeKey: 'dispatches' },
    { name: 'Insights', href: '/insights', icon: HiTrendingUp, feature: 'analytics' },
    { name: 'Control Tower', href: '/control-tower', icon: HiGlobeAlt, feature: 'audit_trail' },
    { name: 'Audit Trail', href: '/audit', icon: HiClipboardList, feature: 'audit_trail' },
    { name: 'Knowledge', href: '/knowledge', icon: HiLightBulb, feature: 'knowledge_curation' },
    { name: 'API Tokens', href: '/api-tokens', icon: HiKey },
    { name: 'Settings', href: '/settings', icon: HiCog },
  ];

  const handleLogout = async () => {
    await logout();
    navigate('/signin');
  };

  const getTierBadgeColor = (tier: string) => {
    switch (tier) {
      case 'enterprise':
        return 'bg-purple-100 text-purple-800';
      case 'professional':
        return 'bg-blue-100 text-blue-800';
      case 'team':
      case 'starter': // Legacy support
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {showSplash && <WeeklySplash onDismiss={() => setShowSplash(false)} />}

      {/* Top Navigation */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <Link to="/dashboard" className="flex items-center">
                <span className="text-2xl font-bold text-mindmeld-primary">MindMeld</span>
                <span className="ml-2 text-sm text-gray-500">Admin</span>
              </Link>
              <LocalEnvironmentBadge className="ml-3" />
            </div>
            <div className="flex items-center space-x-4">
              {client && (
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTierBadgeColor(client.subscription_tier)}`}>
                  {client.subscription_tier.charAt(0).toUpperCase() + client.subscription_tier.slice(1)}
                </span>
              )}
              <span className="text-sm text-gray-600">{user?.email}</span>
              <button
                onClick={handleLogout}
                className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100"
                title="Sign out"
              >
                <HiLogout className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex">
        {/* Sidebar Navigation */}
        <aside className="w-64 min-h-screen bg-white shadow-sm">
          <nav className="mt-5 px-2">
            <div className="space-y-1">
              {navItems.map((item) => {
                // Check feature access for enterprise features
                if (item.feature && !hasFeature(item.feature)) {
                  return null;
                }

                const isActive = location.pathname === item.href;
                const Icon = item.icon;

                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md ${
                      isActive
                        ? 'bg-mindmeld-primary text-white'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon
                      className={`mr-3 h-5 w-5 ${
                        isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'
                      }`}
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.badgeKey && badgeCounts[item.badgeKey] > 0 && (
                      <span className={`ml-auto px-1.5 py-0.5 text-xs font-medium rounded-full ${
                        isActive ? 'bg-white/20 text-white' : 'bg-red-100 text-red-700'
                      }`}>
                        {badgeCounts[item.badgeKey]}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
